import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const logoUrl = './kapoorandsons_logo.png';
 
  useEffect(() => {
    // For non-home routes we always show the opaque navbar
    if (location.pathname !== '/') {
      setScrolled(true);
      return;
    }

    // On home page, prefer observing the hero section instead of raw scrollY.
    // This ensures the navbar stays transparent while the hero is visible,
    // and becomes opaque once the hero is out of view.

    const checkHeroAndSetup = () => {
      const hero = document.querySelector('.hero-section');

      if (!hero) {
        // If hero not found, retry after a short delay (for lazy loading)
        setTimeout(checkHeroAndSetup, 100);
        return;
      }

      // Initial check: determine if hero is currently visible
      const rect = hero.getBoundingClientRect();
      const isHeroVisible = rect.bottom > 80; // 80px is navbar height approx
      setScrolled(!isHeroVisible);

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          // When the hero is intersecting (visible) we want transparent navbar
          // so scrolled = false. When hero is NOT intersecting, make navbar opaque.
          setScrolled(!entry.isIntersecting);
        },
        {
          root: null,
          threshold: 0,
          // Adjust rootMargin so the switch happens when hero moves under the navbar
          rootMargin: '-80px 0px 0px 0px'
        }
      );

      observer.observe(hero);

      return () => observer.disconnect();
    };

    // Start checking for hero element
    const cleanup = checkHeroAndSetup();

    return cleanup;
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/products', label: 'Products' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-[#1a1c1b] bg-opacity-95 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.3)] border-b border-[#3f4816]' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[87.5rem] mx-auto px-4 sm:px-6 md:px-8 lg:px-10">
        <div className="flex justify-between items-center h-16 sm:h-18 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Kapoor & Sons"
                className="h-12 sm:h-13 md:h-14 w-auto rounded-md object-contain"
                style={{ borderRadius: '50%' }}
              />
            ) : null}
            <div className="text-[#d9fb06] font-bold text-lg sm:text-xl md:text-2xl whitespace-nowrap">
              Kapoor & Sons
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${
                  location.pathname === link.path
                    ? 'text-[#d9fb06]'
                    : 'text-[#dfddd6]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="btn-primary ml-2 lg:ml-4 text-sm lg:text-base px-3 md:px-4 lg:px-6 whitespace-nowrap"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-[#d9fb06] p-2 hover:bg-[#3f4816] rounded-lg transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden backdrop-blur-md border-t border-[#3f4816]">
          <div className="px-4 sm:px-6 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 px-4 rounded-lg transition-colors ${
                  location.pathname === link.path
                    ? 'bg-[#3f4816] text-[#d9fb06]'
                    : 'text-[#dfddd6] hover:bg-[#302f2c]'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="btn-primary w-full text-center block mt-4"
              onClick={() => setIsOpen(false)}
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
